/**
 * collection.js - Collection Page Interactions (v2)
 * 
 * Features:
 * - Card price enhancement from metafields priceMap (server fallback via product.price)
 * - Mobile filter panel toggle with active count badge
 * - Desktop sort dropdown + mobile sort pills
 * - Quick Add Drawer (via data-quick-add event delegation)
 * - Wishlist toggle
 * - Pagination go-to-page
 * - Lazy load images
 * 
 * Dependencies:
 * - Tailwind CSS (via theme)
 * - jw-components.js (Quick Add Drawer)
 */

(function() {
  'use strict';

  // ============================================
  // Utility Functions
  // ============================================

  /**
   * Debounce function for performance
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Show toast notification
   */
  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full text-white text-sm font-medium shadow-lg transform transition-all duration-300';
    toast.classList.add(type === 'success' ? 'bg-primary' : 'bg-red-500');
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('opacity-0', 'translate-y-2');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  // ============================================
  // Initialize All Collection Features
  // ============================================

  function initCollection() {
    initFilterPanel();
    initSorting();
    initPagination();
    initCardPrices();
    initQuickAddButtons();
    initWishlist();
  }

  // ============================================
  // Runtime Store（collection-runtime 单例解析）
  // ============================================

  // 一次解析：collection-runtime JSON → 内存单例，所有消费者共享
  let runtimeStore = null;

  function getRuntimeStore() {
    if (runtimeStore) return runtimeStore;

    var el = document.getElementById('collection-runtime');
    if (!el) {
      runtimeStore = { collection_id: null, products: {} };
      return runtimeStore;
    }

    try {
      runtimeStore = JSON.parse(el.textContent);
    } catch (e) {
      runtimeStore = { collection_id: null, products: {} };
    }

    return runtimeStore;
  }

  // 按 product.id 取单个产品元数据（5 字段：free_shipping/moq/prices/sku_images/image_binding）
  function getProductMeta(productId) {
    return getRuntimeStore().products[productId] || {};
  }

  // ============================================
  // Card Prices from Metafields priceMap
  // ============================================

  /**
   * initCardPrices - Enhance prices with B2B tier data from metafields
   * 
   * Server has already rendered product.price as fallback.
   * JS enhances: shows tier-specific price range + discount badge + compare_at strikethrough.
   */
  function initCardPrices() {
    var cards = document.querySelectorAll('.collection-product-card');

    cards.forEach(function(card) {
      var productId = card.getAttribute('data-product-id');
      if (!productId) return;

      var metaData = getProductMeta(productId);
      if (!metaData || typeof metaData !== 'object') return;

      var prices = metaData.prices;
      if (!prices || typeof prices !== 'object') return;

      var skuKeys = Object.keys(prices);
      if (skuKeys.length === 0) return;

      var minPrice = Infinity;
      var maxPrice = -Infinity;
      var maxPercent = 0;

      skuKeys.forEach(function(skuKey) {
        var skuData = prices[skuKey];
        if (!skuData || !skuData.tiers || !skuData.tiers.length) return;

        var tierPrice = parseFloat(skuData.tiers[0].price);
        if (isNaN(tierPrice)) return;

        if (tierPrice < minPrice) minPrice = tierPrice;
        if (tierPrice > maxPrice) maxPrice = tierPrice;

        // Price Contract v1.1: Marketing Engine — discount from compare_at_price ONLY
        var priceBase = parseFloat(skuData.price_base);
        var percent = 0;
        var compareAt = parseFloat(skuData.compare_at_price);
        if (!isNaN(compareAt) && compareAt > priceBase && priceBase > 0) {
          percent = Math.round((1 - priceBase / compareAt) * 100);
        }
        if (percent > maxPercent) maxPercent = percent;
      });

      // Floor: Shopify's lowest variant price (covers SKUs without tiers)
      var shopifyPrice = parseFloat(card.getAttribute('data-product-price')) || 0;
      if (shopifyPrice > 0) {
        var shopifyDollars = shopifyPrice / 100;
        if (minPrice === Infinity || shopifyDollars < minPrice) minPrice = shopifyDollars;
        if (maxPrice === -Infinity || shopifyDollars > maxPrice) maxPrice = shopifyDollars;
      }

      // Update price range display (overrides server fallback)
      var priceRangeEl = card.querySelector('[data-card-price-range]');
      if (priceRangeEl && minPrice !== Infinity) {
        if (Math.abs(minPrice - maxPrice) < 0.005) {
          priceRangeEl.textContent = '$' + minPrice.toFixed(2);
        } else {
          priceRangeEl.textContent = '$' + minPrice.toFixed(2) + '-' + maxPrice.toFixed(2);
        }
      }


      // Update compare-at strikethrough (native variant price = 划线价)
      var compareEl = card.querySelector('[data-card-compare-price]');
      if (compareEl && maxPercent > 0) {
        var nativePrice = parseInt(card.getAttribute('data-product-price')) || 0;
        if (nativePrice > 0 && minPrice < nativePrice / 100) {
          compareEl.textContent = '$' + (nativePrice / 100).toFixed(2);
          compareEl.classList.remove('hidden');
        }
      }
    });
  }


  // ============================================
  // Filter Panel (Mobile Drawer)
  // ============================================

  function initFilterPanel() {
    var filterToggle = document.querySelector('[data-filter-toggle]');
    var filterPanel = document.querySelector('[data-filter-panel]');
    var filterCloseButtons = document.querySelectorAll('[data-filter-close]');
    var filterForm = document.querySelector('[data-filter-form]');

    if (!filterPanel) return;

    // Toggle filter panel
    if (filterToggle) {
      filterToggle.addEventListener('click', function() {
        filterPanel.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        // Animate slide-in
        var content = filterPanel.querySelector('[data-filter-content]');
        if (content) {
          requestAnimationFrame(function() {
            content.classList.remove('-translate-x-full');
            content.classList.add('translate-x-0');
          });
        }
      });
    }

    // Close filter panel
    filterCloseButtons.forEach(function(btn) {
      btn.addEventListener('click', function() {
        closeFilterPanel(filterPanel);
      });
    });

    // Close on backdrop click
    var backdrop = filterPanel.querySelector('[data-filter-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', function() {
        closeFilterPanel(filterPanel);
      });
    }

    // Auto-submit filter form on checkbox change
    if (filterForm) {
      filterForm.querySelectorAll('input[type="checkbox"]').forEach(function(input) {
        input.addEventListener('change', debounce(function() {
          var form = filterForm.querySelector('form') || filterForm;
          if (form && form.tagName === 'FORM') {
            form.submit();
          }
        }, 500));
      });
    }

    // Price range inputs
    filterPanel.querySelectorAll('input[name*="filter."]').forEach(function(input) {
      input.addEventListener('change', debounce(function() {
        var form = filterPanel.querySelector('form');
        if (form) {
          form.submit();
        }
      }, 800));
    });
  }

  function closeFilterPanel(filterPanel) {
    var content = filterPanel.querySelector('[data-filter-content]');
    if (content) {
      content.classList.remove('translate-x-0');
      content.classList.add('-translate-x-full');
    }
    setTimeout(function() {
      filterPanel.classList.add('hidden');
      document.body.style.overflow = '';
    }, 300);
  }

  // ============================================
  // Sorting
  // ============================================

  function initSorting() {
    var sortSelect = document.querySelector('[data-sort-select]');
    var sortButtons = document.querySelectorAll('[data-sort-filter]');

    if (sortSelect) {
      sortSelect.addEventListener('change', function() {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', this.value);
        window.location.href = url.toString();
      });
    }

    // Mobile sort filter buttons
    if (sortButtons.length > 0) {
      sortButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          var sortValue = this.getAttribute('data-sort-filter');
          var url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortValue);
          window.location.href = url.toString();
        });
      });
    }
  }

  // ============================================
  // Pagination
  // ============================================

  function initPagination() {
    var gotoInput = document.querySelector('[data-goto-page]');

    if (gotoInput) {
      gotoInput.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') {
          var page = parseInt(this.value, 10);
          var maxPage = parseInt(this.getAttribute('data-max-page') || '100', 10);
          
          if (page > 0 && page <= maxPage) {
            var url = new URL(window.location.href);
            url.searchParams.set('page', page);
            window.location.href = url.toString();
          } else {
            showToast('Please enter a valid page number', 'error');
          }
        }
      });
    }
  }

  // ============================================
  // Wishlist Toggle
  // ============================================

  // ============================================
  // Quick Add Buttons (direct binding)
  // ============================================

  function initQuickAddButtons() {
    document.querySelectorAll('[data-quick-add]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        var productId = this.getAttribute('data-quick-add');
        if (!productId) return;
        var card = this.closest('.collection-product-card');
        var preRenderedData = { title: '', moq: '1', tags: [], prices: {}, skuImages: {}, imageBinding: null, freeShipping: false };
        if (card) {
          preRenderedData.title = card.getAttribute('data-product-title') || '';
          preRenderedData.moq = card.getAttribute('data-moq') || '1';
          preRenderedData.tags = (card.getAttribute('data-tags') || '').split(',').filter(function(t) { return t.trim(); });
          var metaData = getProductMeta(productId);
          if (metaData && typeof metaData === 'object') {
            preRenderedData.freeShipping = metaData.free_shipping || false;
            if (metaData.moq) preRenderedData.moq = metaData.moq.toString();
            if (metaData.prices && typeof metaData.prices === 'object') preRenderedData.prices = metaData.prices;
            if (metaData.sku_images && typeof metaData.sku_images === 'object') preRenderedData.skuImages = metaData.sku_images;
            // Phase F1.2: 上行 image_binding 给 Quick Add resolver
            preRenderedData.imageBinding = metaData.image_binding || null;
          }
        }
        // 用 handle 打开 drawer（drawer 内部按 handle fetch /products/{handle}.js）
        var handle = card ? card.getAttribute('data-product-handle') : '';
        if (window.openQuickAddDrawer) window.openQuickAddDrawer(handle, preRenderedData);
      });
    });
  }

  function initWishlist() {
    document.querySelectorAll('[data-wishlist]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        var productId = this.getAttribute('data-wishlist');
        var icon = this.querySelector('svg');
        
        if (!icon) return;

        // Toggle wishlist state (visual only - connect to app for persistence)
        var isFilled = icon.getAttribute('fill') === 'currentColor';
        
        if (isFilled) {
          icon.setAttribute('fill', 'none');
          icon.classList.remove('text-accent');
          icon.classList.add('text-gray-600');
          showToast('Removed from wishlist');
        } else {
          icon.setAttribute('fill', 'currentColor');
          icon.classList.remove('text-gray-600');
          icon.classList.add('text-accent');
          showToast('Added to wishlist!');
        }

        // Dispatch custom event for external wishlist app integration
        window.dispatchEvent(new CustomEvent('wishlist:toggle', {
          detail: {
            productId: productId,
            action: isFilled ? 'remove' : 'add'
          }
        }));
      });
    });
  }

  // ============================================
  // Lazy Load Images (Intersection Observer)
  // ============================================

  function initLazyLoad() {
    if ('IntersectionObserver' in window) {
      var imageObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      document.querySelectorAll('img[data-src]').forEach(function(img) {
        imageObserver.observe(img);
      });
    }
  }

  // ============================================
  // Initialize on DOM Ready
  // ============================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initCollection();
      initLazyLoad();
    });
  } else {
    initCollection();
    initLazyLoad();
  }

})();


